import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EmbeddedViewRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DatePickerComponent, DatePickerModule, TimePickerModule } from '@progress/kendo-angular-dateinputs';
import { QPointIconModule } from '@qpoint/icons';
import { isSameDay, isValid, set, setYear } from 'date-fns';

import { QPointTimePickerOverrideDirective } from '../time-picker/time-picker-override.directive';

export interface IDateTime {
  date: Date;
  time: Date;
}

@Component({
    selector: 'qpoint-datetime-picker',
    templateUrl: './datetime-picker.component.html',
    styleUrls: ['./datetime-picker.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => QPointDateTimePickerComponent),
            multi: true
        }
    ],
    imports: [
        DatePickerModule,
        CommonModule,
        TimePickerModule,
        TranslateModule,
        QPointIconModule,
        QPointTimePickerOverrideDirective
    ]
})
export class QPointDateTimePickerComponent implements ControlValueAccessor, AfterViewInit {
  @Input() public steps: any = { hour: 1, minute: 15 };
  @Input() public min: Date = new Date(1900, 0, 1);
  @Input() public max: Date;
  @Input() public disabled: boolean;
  @Input() readonly = false;
  @Input() dateReadonly = false;
  @Input() public format: string = null;
  @Input() public dayOnly = false;
  @Input('clearable') set clearableSetter(value: boolean) {
    this.clearable = value;
    this.setupClearButton();
  }
  @Input() public cancelToolTip = 'common.datetimePicker.Cancel changes';
  @Input() public acceptToolTip = 'common.datetimePicker.Set time';
  @Input() public cancelLabel = 'common.datetimePicker.Cancel';
  @Input() public acceptLabel = 'common.datetimePicker.Set';
  @Output() public touch: EventEmitter<any> = new EventEmitter();
  public date: Date;
  public time: Date;
  clearable = false;

  get timePickerMin() {
    return isSameDay(this.min, this.date) ? this.min : null;
  }

  private clearButtonEmbeddedView: EmbeddedViewRef<any> | null = null;
  @ViewChild(DatePickerComponent) kendoDatePicker!: DatePickerComponent;
  @ViewChild('clearButtonTemplate') clearButtonTemplate!: TemplateRef<any>;

  constructor() {
  }

  ngAfterViewInit() {
    this.setupClearButton();
  }

  private setupClearButton() {
    const wrapper = this.kendoDatePicker?.wrapper;
    if (wrapper && this.clearButtonTemplate && this.clearable) {
      if(!this.clearButtonEmbeddedView) {
        const el = wrapper.nativeElement as HTMLElement;
        this.clearButtonEmbeddedView = this.clearButtonTemplate.createEmbeddedView({});
        this.clearButtonEmbeddedView.detectChanges();
        el.children[0].append(this.clearButtonEmbeddedView.rootNodes[0]);
      }
    } else if (this.clearButtonEmbeddedView) {
      this.clearButtonEmbeddedView.destroy();
      this.clearButtonEmbeddedView = null;
      const el = wrapper.nativeElement as HTMLElement;
      el.children[0].children[1].remove();
    }
  }

  public dateOnChange(date: Date) {
    if (!this.time) {
      this.time = date;
    }
    this.dateTimeValueChanged();
  }

  public timeOnChange(date: Date) {
    this.dateTimeValueChanged();
  }

  public dateTimeValueChanged() {
    let currentDate = this.date;
    if (currentDate?.getFullYear() < 999) {
      currentDate = setYear(currentDate, currentDate.getFullYear() + (Math.floor(new Date().getFullYear() / 100) * 100));
    }

    if (isValid(currentDate)) {
      this.date = currentDate;
    }

    const currentTime = this.time;
    if (currentTime) {
      currentDate = set(currentDate, {
        hours: currentTime.getHours(),
        minutes: currentTime.getMinutes()
      });
    } else {
      currentDate = set(currentDate, {
        hours: null,
        minutes: null
      });    
    }

    this.onChange(this.time && this.date ? currentDate : null);
  }

  // Should be called whenever something in the view has changed
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // Is used whenever the control receives a touch event
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // Is used whenever the disable status of the input is changed
  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.disabled = true;
    } else {
      this.disabled = false;
    }
    this.setupClearButton();
  }

  // Is used whenever the model has been updated, to update the view
  public writeValue(obj: Date): void {
    this.date = obj;
    this.time = obj;
    this.kendoDatePicker?.writeValue(this.date);
  }

  public onChange = (_: any) => {
  }
  public onTouched = () => {
  }

  clearValue() {
    if (!this.disabled && this.clearable) {
      this.date = null;
      this.onChange(this.date);
    }
  }

  public handleOnTouched() {
    this.touch.emit();

    if (this.onTouched) {
      this.onTouched();
    }
  }  
}
